import { useMemo, useState } from "react";

import { Button, Classes, Colors, Position } from "@blueprintjs/core";
import styled from "@emotion/styled";
import {
  addDays,
  addMilliseconds,
  addMinutes,
  differenceInMilliseconds,
  formatISO,
  getHours,
  isAfter,
  isBefore,
  subDays,
} from "date-fns";
import { toZonedTime } from "date-fns-tz";

import CalendarIcon from "@/../../hypetrain/static/img/calendar_black.svg";
import CalendarGrayIcon from "@/../../hypetrain/static/img/calendar-gray.svg";
import GoogleCalendarIcon from "@/../../hypetrain/static/img/google_calendar.svg";
import OutlookIcon from "@/../../hypetrain/static/img/outlook-icon.svg";
import { Popover } from "@/components/helpers/ui/blueprint-overrides/Popover";
import { EmbedSvg } from "@/components/helpers/ui/EmbedSvg";
import { Box, Flex } from "@/components/layout/flexbox";
import { BP_NS, BRAND_PURPLE, CARD_CLASSES } from "@/css/constants";
import {
  LeadComponent,
  MainComponent,
  ParticipantComponent,
} from "@/types/programs";
import { formatDate, getBrowserTimezone, setTime } from "@/utils/datetime";

import { getCloseDate } from "./utils";
import { generateAllCalendarData } from "./utils/calendar-utils";

type ProgramComponentType =
  | LeadComponent
  | MainComponent
  | ParticipantComponent;

function TimeContent({ component }: { component: ProgramComponentType }) {
  const [selectedDuration, setSelectedDuration] = useState(30);
  const [selectedTimezone] = useState(getBrowserTimezone());
  const timeConfigs = [15, 30, 60];

  // Convert all dates to the selected timezone
  const closeDate = getCloseDate(component);
  const componentEndDate = closeDate
    ? formatISO(toZonedTime(closeDate, selectedTimezone))
    : null;

  const componentStartDate = component.targetStartDate
    ? formatISO(toZonedTime(component.targetStartDate, selectedTimezone))
    : null;

  const componentUrl = `${window.location.origin}/programs/${component.programId}`;
  const description = component.participantGuidance;
  const title = `Complete ${component.name}`;

  const parsedStartDate = componentStartDate
    ? toZonedTime(componentStartDate, selectedTimezone)
    : null;

  const parsedEndDate = componentEndDate
    ? toZonedTime(componentEndDate, selectedTimezone)
    : null;

  const suggestedStartDate = useMemo(() => {
    if (!parsedStartDate || !parsedEndDate) {
      console.error("Invalid date provided to AddToCalendarButton");
      return new Date();
    }

    const now = toZonedTime(new Date(), selectedTimezone);

    if (isAfter(now, parsedStartDate)) {
      const zonedDate = toZonedTime(parsedStartDate, selectedTimezone);
      return setTime(zonedDate, { hours: 10 });
    }

    if (isAfter(now, parsedStartDate) && isBefore(now, parsedEndDate)) {
      // Add 1 day at 10:00 AM
      const suggested = setTime(
        addDays(toZonedTime(now, selectedTimezone), 1),
        { hours: 10 },
      );

      // Calculate time windows for comparison
      const timeWindow = differenceInMilliseconds(parsedEndDate, now);
      const suggestedDelay = differenceInMilliseconds(suggested, now);

      if (suggestedDelay > timeWindow * 0.75) {
        const betterDelay = timeWindow * 0.25;
        const betterTime = addMilliseconds(
          toZonedTime(now, selectedTimezone),
          betterDelay,
        );

        if (getHours(betterTime) < 9 || getHours(betterTime) > 17) {
          return setTime(betterTime, { hours: 10 });
        }

        return betterTime;
      }

      const endBufferDate = subDays(
        toZonedTime(parsedEndDate, selectedTimezone),
        1,
      );

      if (isAfter(suggested, endBufferDate)) {
        return setTime(toZonedTime(endBufferDate, selectedTimezone), {
          hours: 10,
        });
      }

      return suggested;
    }

    return toZonedTime(parsedEndDate, selectedTimezone);
  }, [parsedStartDate, parsedEndDate, selectedTimezone]);

  const generateCalendarData = (duration: number) => {
    const startDateTime = toZonedTime(suggestedStartDate, selectedTimezone);
    const endDateTime = addMinutes(startDateTime, duration);

    const baseDetails = [
      description,
      "",
      `⏰ Must be completed by ${formatDate(parsedEndDate, "eee, MMM d, yyyy h:mm a z")}`,
      `🌍 Scheduled in ${selectedTimezone} timezone`,
      "",
      "💡 Feel free to adjust the date and time to fit your schedule.",
    ];

    const googleDetails = [
      ...baseDetails,
      "",
      "🔗 Component Link:",
      componentUrl,
    ].join("\n");

    const outlookDetails = [
      description,
      "<br>",
      `⏰ Must be completed by ${formatDate(parsedEndDate, "eee, MMM d, yyyy h:mm a z")}`,
      `🌍 Scheduled in ${selectedTimezone} timezone`,
      "<br>",
      "💡 Feel free to adjust the date and time to fit your schedule.",
      "<br>",
      "🔗 Access the component at:",
      `<a href="${componentUrl}">${componentUrl}</a>`,
    ].join("<br>");

    return generateAllCalendarData({
      title,
      description: description || "",
      meetingUrl: componentUrl,
      componentUrl,
      startDate: startDateTime,
      endDate: endDateTime,
      timezone: selectedTimezone,
      attendees: [], // No attendees for schedule time button
      googleDetails,
      outlookDetails,
    });
  };

  const handleCalendarSelect = (service: string) => {
    const calendarData = generateCalendarData(selectedDuration);
    if (service === "apple") {
      const link = document.createElement("a");
      link.href = calendarData.ics;
      link.download = "event.ics";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } else {
      window.open(calendarData[service as keyof typeof calendarData], "_blank");
    }
  };

  const calendarConfigs = [
    {
      name: "Google Calendar",
      icon: GoogleCalendarIcon,
      onClick: () => handleCalendarSelect("google"),
    },
    {
      name: "Outlook Calendar",
      icon: OutlookIcon,
      onClick: () => handleCalendarSelect("outlook"),
    },
    {
      name: "Apple Calendar / Other",
      icon: CalendarIcon,
      onClick: () => handleCalendarSelect("apple"),
    },
  ];

  return (
    <Box p={16} width={300}>
      <Box fontWeight="600">Time to set aside:</Box>
      <Flex justifyContent="space-between" mt={12} style={{ gap: 8 }}>
        {timeConfigs.map((time) => (
          <StyledButton
            key={time}
            onClick={() => {
              setSelectedDuration(time);
            }}
            active={selectedDuration === time}
            color="#3788EE"
          >
            {time}m
          </StyledButton>
        ))}
      </Flex>
      <Box fontWeight="600" mt={24}>
        Add to calendar:
      </Box>
      <Flex flexDirection="column" style={{ gap: 8 }}>
        {calendarConfigs.map((config) => (
          <CalendarButton
            key={config.name}
            icon={<EmbedSvg width={18} height={18} src={config.icon} />}
            onClick={config.onClick}
          >
            {config.name}
          </CalendarButton>
        ))}
      </Flex>
    </Box>
  );
}

export function ScheduleTimeButton({
  component,
}: {
  component: LeadComponent | MainComponent | ParticipantComponent;
}) {
  const [isOpen, setIsOpen] = useState(false);
  return (
    <>
      <Popover
        canEscapeKeyClose={true}
        position={Position.BOTTOM_RIGHT}
        onClose={() => {
          setIsOpen(false);
        }}
        isOpen={isOpen}
        content={<TimeContent component={component} />}
        className="rct-item-content"
      >
        <Button
          icon={<EmbedSvg width={14} height={14} src={CalendarGrayIcon} />}
          className={CARD_CLASSES}
          style={{
            fontSize: 14,
            padding: "8px 20px",
          }}
          onClick={() => {
            setIsOpen(true);
          }}
        >
          Schedule Time
        </Button>
      </Popover>
    </>
  );
}

const StyledButton = styled(Button)`
  &.${Classes.BUTTON}{
    border: 1px solid #e2e2e2;
    box-shadow: none;
    width: 100%;
    }
  }
  &.${Classes.BUTTON}:not([class*='${BP_NS}-intent-']) {
    background-color: ${Colors.WHITE};
    box-shadow: none;
    &:hover {
      box-shadow: none;
      background-color: ${Colors.WHITE};
      color: ${BRAND_PURPLE};
      border: 1px solid  ${BRAND_PURPLE};
    }
    &.${BP_NS}-active {
      color: ${BRAND_PURPLE};
      border: 1px solid ${BRAND_PURPLE};
      box-shadow: none;
    }
  }
  :focus {
    outline: none;
    box-shadow: none;
    outline-offset: 0;
  }
`;

const CalendarButton = styled(Button)`
  &.${Classes.BUTTON} {
    border: 1px solid #e2e2e2;
    box-shadow: none;
    justify-content: start;
    &:hover {
      box-shadow: none;
      background-color: ${Colors.WHITE};
      color: ${BRAND_PURPLE};
    }
  }

  &.${Classes.BUTTON}:not([class*='${BP_NS}-intent-']) {
    background-color: ${Colors.WHITE};
    box-shadow: none;
    &:hover {
      box-shadow: none;
      background-color: ${Colors.WHITE};
      color: ${BRAND_PURPLE};
      border: 1px solid ${BRAND_PURPLE};
    }
    &.${BP_NS}-active {
      color: ${BRAND_PURPLE};
      border: 1px solid ${BRAND_PURPLE};
    }
  }
  :focus {
    outline: none;
    box-shadow: none;
    outline-offset: 0;
  }
`;
